import type { LanguageType } from '@repo/sdk'
import type { inferRouterOutputs } from '@trpc/server'
import type { AppRouter } from 'api/src/types'
import { useQuery } from '@tanstack/react-query'
import {
  ArrowRight,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Search,
  X,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import { useI18n } from '@/lib/i18n'
import { returnTargetFrom } from '@/lib/return-target'
import { useTRPC } from '@/lib/trpc'
import {
  readTournamentHallState,
  tournamentHallReturnTarget,
  tournamentHallTabs,
  type TournamentHallTab,
} from './archive-state'
import { TournamentRulesViewer } from './rules-viewer'
import { tournamentDateRange, tournamentUrl } from './edition'
import { TournamentModuleNavigation } from './module-navigation'

type Edition = NonNullable<
  inferRouterOutputs<AppRouter>['tournamentRouter']['getEdition']
>
type ArchiveRecord
  = inferRouterOutputs<AppRouter>['tournamentRouter']['getArchive']['records'][number]
type HallContent
  = inferRouterOutputs<AppRouter>['tournamentRouter']['getHallContent']

const tabKeys = {
  results: 'tournamentHall.resultsTab',
  highlights: 'tournamentHall.highlightsTab',
  games: 'tournamentHall.allGamesTab',
  rules: 'tournamentRules.title',
} as const

const phaseKeys = {
  UNSCHEDULED: 'tournament.placeholderState',
  UPCOMING_REGISTRATION: 'tournament.upcoming',
  REGISTRATION_OPEN: 'tournament.registrationOpen',
  WAITING_FOR_COMPETITION: 'tournament.waiting',
  COMPETITION_OPEN: 'tournament.competitionOpen',
  ENDED: 'tournamentHall.editionEnded',
} as const

const focusClassName
  = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background'
const controlClassName = `min-h-11 rounded-lg border border-border bg-muted/50 px-3 py-2 font-semibold text-foreground transition-colors hover:bg-card disabled:opacity-50 ${focusClassName}`

export function TournamentHallSupplement({
  edition,
  tab,
  onRules,
  content,
  loading,
  failed,
  onRetry,
  focusFullRules,
}: {
  edition: Edition
  tab: Exclude<TournamentHallTab, 'games'>
  onRules: () => void
  content: HallContent | undefined
  loading: boolean
  failed: boolean
  onRetry: () => void
  focusFullRules: boolean
}) {
  const { t } = useI18n()
  const location = useLocation()
  if (tab === 'rules') {
    return (
      <TournamentRulesViewer
        key={edition.id}
        editionId={edition.id}
        variant="hall"
        onFullRules={onRules}
        focusFullRules={focusFullRules}
      />
    )
  }
  if (failed) {
    return (
      <div
        role="alert"
        data-testid="tournament-hall-content-error"
        className="py-8 text-center"
      >
        <p>{t('forum.errorGeneric')}</p>
        <button
          type="button"
          data-testid="tournament-hall-content-retry"
          onClick={onRetry}
          className={`${controlClassName} mt-3`}
        >
          {t('play.retry.button')}
        </button>
      </div>
    )
  }
  if (loading || !content) {
    return (
      <p
        role="status"
        data-testid="tournament-hall-content-loading"
        className="flex items-center justify-center gap-2 py-8"
      >
        <Loader2 className="size-5 animate-spin" aria-hidden="true" />
        {t('forum.loading')}
      </p>
    )
  }
  const published
    = edition.lifecycle.publicGamesAvailable && content.publicGamesAvailable
  if (tab === 'results') {
    if (
      published
      && content.results.state === 'PUBLISHED'
      && content.results.content
    ) {
      return (
        <section
          className="space-y-5 py-3"
          data-testid="tournament-hall-results-published"
          data-edition-id={edition.id}
        >
          <h3 className="text-2xl font-semibold">
            {t('tournamentHall.resultsTab')}
          </h3>
          <article
            lang={content.results.contentLanguage ?? undefined}
            className="min-w-0 space-y-4 rounded-lg border border-border bg-muted/50 p-4 sm:p-5"
          >
            <h4
              data-testid="tournament-hall-results-title"
              className="break-words text-xl font-semibold"
            >
              {content.results.content.title}
            </h4>
            <p
              data-testid="tournament-hall-results-body"
              className="whitespace-pre-line break-words leading-7 [overflow-wrap:anywhere]"
            >
              {content.results.content.body}
            </p>
          </article>
        </section>
      )
    }
    return (
      <p
        role="status"
        className="flex min-h-20 items-center py-5 text-sm text-muted-foreground sm:min-h-[92px]"
        data-testid="tournament-hall-results-pending"
      >
        {t('tournamentHall.resultsPending')}
      </p>
    )
  }
  if (
    published
    && content.highlights.state === 'PUBLISHED'
    && content.highlights.items.length > 0
  ) {
    return (
      <section
        className="space-y-5 py-3"
        data-testid="tournament-hall-highlights"
        data-edition-id={edition.id}
      >
        <h3 className="text-2xl font-semibold">
          {t('tournamentHall.highlightsTab')}
        </h3>
        <ul className="grid min-w-0 gap-4 xl:grid-cols-2">
          {content.highlights.items.map(item => (
            <li
              key={item.gameId}
              data-testid="tournament-hall-highlight"
              data-edition-id={edition.id}
              data-game-id={item.gameId}
              lang={item.contentLanguage}
              className="min-w-0 rounded-lg border border-border bg-muted/50 p-4 sm:p-5"
            >
              <h4 className="break-words text-xl font-semibold">
                {item.title}
              </h4>
              <p className="mt-3 whitespace-pre-line break-words leading-7 text-muted-foreground [overflow-wrap:anywhere]">
                {item.description}
              </p>
              <Link
                data-testid="tournament-hall-highlight-replay"
                to={`/play/${item.gameId}?${new URLSearchParams({ tournament: edition.id, view: 'god', returnTo: returnTargetFrom(location) })}`}
                className={`${controlClassName} mt-5 flex items-center justify-center gap-2 text-primary`}
              >
                {t('publicPlayerProfile.viewGame')}
                <ChevronRight className="size-5 shrink-0" aria-hidden="true" />
              </Link>
            </li>
          ))}
        </ul>
      </section>
    )
  }
  return (
    <p
      role="status"
      className="flex min-h-20 items-center py-5 text-sm text-muted-foreground sm:min-h-[92px]"
      data-testid="tournament-hall-highlights-pending"
    >
      {t('tournamentHall.highlightsPending')}
    </p>
  )
}

export function TournamentHall() {
  const { language, t } = useI18n()
  const location = useLocation()
  const navigate = useNavigate()
  const trpc = useTRPC()
  const state = readTournamentHallState(location.search)
  const params = new URLSearchParams(location.search)
  const requestedRules
    = params.get('view') === 'rules' || params.get('tournamentView') === 'rules'
  const explicitView
    = (tournamentHallTabs as readonly string[]).includes(params.get('tab') ?? '')
      || requestedRules
      || params.has('search')
      || params.has('page')
  const selectedTab = requestedRules ? 'rules' : state.tab
  const [draftSearch, setDraftSearch] = useState(state.search)
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([])
  const searchRef = useRef<HTMLInputElement>(null)
  const editionsQuery = useQuery({
    ...trpc.tournamentRouter.listEditions.queryOptions({
      language: language as LanguageType,
    }),
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
  })
  const edition = editionsQuery.isError
    ? undefined
    : state.editionId
      ? editionsQuery.data?.find(item => item.id === state.editionId)
      : explicitView
        ? editionsQuery.data?.[0]
        : undefined
  const selectedEditionId = edition?.id
  const hallContentQuery = useQuery({
    ...trpc.tournamentRouter.getHallContent.queryOptions({
      editionId: selectedEditionId ?? '',
      language: language as LanguageType,
    }),
    enabled: !!selectedEditionId,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: 30_000,
  })
  const hallContent
    = hallContentQuery.data?.editionId === selectedEditionId
      && !hallContentQuery.isError
      ? hallContentQuery.data
      : undefined
  const archiveQuery = useQuery({
    ...trpc.tournamentRouter.getArchive.queryOptions({
      editionId: selectedEditionId ?? '',
      search: state.search,
      page: state.page,
    }),
    enabled: !!selectedEditionId,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
  })
  const archive
    = archiveQuery.data?.editionId === selectedEditionId
      ? archiveQuery.data
      : undefined
  const rulesOpen = requestedRules
  const [rulesFocusEditionId, setRulesFocusEditionId] = useState<string>()
  const previousRulesView = useRef({
    editionId: selectedEditionId,
    tab: selectedTab,
    rulesOpen,
  })

  useEffect(() => {
    const previous = previousRulesView.current
    setRulesFocusEditionId(
      previous.editionId === selectedEditionId
      && previous.tab === 'rules'
      && previous.rulesOpen
      && selectedTab === 'rules'
      && !rulesOpen
        ? selectedEditionId
        : undefined,
    )
    previousRulesView.current = {
      editionId: selectedEditionId,
      tab: selectedTab,
      rulesOpen,
    }
  }, [rulesOpen, selectedEditionId, selectedTab])

  useEffect(() => {
    setDraftSearch(state.search)
  }, [state.search, state.editionId])

  useEffect(() => {
    if (!selectedEditionId || state.editionId)
      return
    const search = new URLSearchParams(location.search)
    search.set('edition', selectedEditionId)
    navigate(
      {
        pathname: location.pathname,
        search: `?${search}`,
        hash: location.hash,
      },
      { replace: true },
    )
  }, [
    selectedEditionId,
    state.editionId,
    location.pathname,
    location.search,
    location.hash,
    navigate,
  ])

  useEffect(() => {
    if (
      !archive
      || archiveQuery.isFetching
      || archiveQuery.isError
      || archive.page === state.page
    ) {
      return
    }
    const search = new URLSearchParams(location.search)
    search.set('page', String(archive.page))
    navigate(
      {
        pathname: location.pathname,
        search: `?${search}`,
        hash: location.hash,
      },
      { replace: true },
    )
  }, [
    archive,
    archiveQuery.isFetching,
    archiveQuery.isError,
    state.page,
    location.pathname,
    location.search,
    location.hash,
    navigate,
  ])

  const { refetch: refetchEditions } = editionsQuery
  const { refetch: refetchArchive } = archiveQuery
  const { refetch: refetchHallContent } = hallContentQuery
  useEffect(() => {
    const transitionAt = edition?.lifecycle.nextTransitionAt
    if (!transitionAt)
      return
    const remaining
      = Date.parse(transitionAt) - Date.parse(edition.lifecycle.serverNow)
    const timer = window.setTimeout(
      () => {
        void refetchEditions()
        void refetchArchive()
        void refetchHallContent()
      },
      Math.min(Math.max(remaining + 100, 100), 2_147_483_647),
    )
    return () => window.clearTimeout(timer)
  }, [edition, refetchEditions, refetchArchive, refetchHallContent])

  const updateState = (changes: {
    editionId?: string | null
    tab?: TournamentHallTab
    search?: string
    page?: number
    rulesOpen?: boolean
  }) => {
    const search = new URLSearchParams(location.search)
    if (changes.editionId !== undefined) {
      if (changes.editionId) {
        search.set('edition', changes.editionId)
      }
      else {
        search.delete('edition')
        search.delete('tab')
      }
      search.delete('search')
      search.delete('page')
      search.delete('view')
      search.delete('tournamentView')
      search.delete('tournamentSection')
    }
    if (changes.tab) {
      search.set('tab', changes.tab)
      search.delete('view')
      search.delete('tournamentView')
      search.delete('tournamentSection')
    }
    if (changes.search !== undefined) {
      if (changes.search)
        search.set('search', changes.search)
      else search.delete('search')
      search.delete('page')
    }
    if (changes.page !== undefined)
      search.set('page', String(changes.page))
    if (changes.rulesOpen === true)
      search.set('tournamentView', 'rules')
    if (changes.rulesOpen === false) {
      search.delete('view')
      search.delete('tournamentView')
      search.delete('tournamentSection')
    }
    navigate({
      pathname: location.pathname,
      search: `?${search}`,
      hash: location.hash,
    })
  }

  const recordState = (record: ArchiveRecord) => {
    if (record.state === 'COMPLETED_WIN')
      return t('tournamentParticipation.win')
    if (record.state === 'COMPLETED_LOSS')
      return t('tournamentParticipation.loss')
    if (record.state === 'FORFEITED_LOSS')
      return t('tournamentParticipation.forfeitedLoss')
    if (record.state === 'VOID')
      return t('tournamentVoid.title')
    return t('dashboard.games.statuses.in_progress')
  }
  const loadingArchive
    = archiveQuery.isPending
      || archiveQuery.isFetching
      || editionsQuery.isFetching
  const publicGamesAvailable
    = edition?.lifecycle.publicGamesAvailable === true
      && archive?.publicGamesAvailable === true
  const invalidEdition
    = editionsQuery.isSuccess && !!state.editionId && !edition

  const contentTabs = tournamentHallTabs.filter(tab => tab !== 'rules')
  const contentAvailable
    = edition?.lifecycle.publicGamesAvailable === true
      && hallContent?.publicGamesAvailable === true

  return (
    <main
      data-testid="tournament-hall"
      data-village-page="tournament-hall"
      dir={language === 'ar' ? 'rtl' : 'ltr'}
      className="min-h-dvh bg-section-dark px-4 pb-10 pt-24 text-foreground sm:px-6 lg:px-8"
    >
      <div className="mx-auto max-w-6xl">
        <TournamentModuleNavigation
          active="hall"
          tournamentPath={tournamentHallReturnTarget(
            location,
            selectedEditionId,
          )}
        />
        <header className="mb-6 sm:mb-7">
          <h1 className="text-[28px] font-bold leading-tight sm:text-3xl">
            {t('tournamentHall.title')}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {t('tournamentHall.subtitle')}
          </p>
        </header>
        <section
          className="min-w-0 overflow-hidden rounded-xl border border-border bg-card"
          aria-labelledby="tournament-hall-editions-title"
        >
          <h2 id="tournament-hall-editions-title" className="sr-only">
            {t('tournamentHall.editions')}
          </h2>
          {editionsQuery.isPending && (
            <p role="status" className="p-6">
              {t('forum.loading')}
            </p>
          )}
          {editionsQuery.isError && (
            <div
              role="alert"
              data-testid="tournament-hall-editions-error"
              className="p-6"
            >
              <p>{t('forum.errorGeneric')}</p>
              <button
                type="button"
                onClick={() => {
                  void refetchEditions()
                }}
                className={`${controlClassName} mt-3`}
                data-testid="tournament-hall-editions-retry"
              >
                {t('play.retry.button')}
              </button>
            </div>
          )}
          {editionsQuery.isSuccess && editionsQuery.data.length === 0 && (
            <p
              role="status"
              data-testid="tournament-hall-no-editions"
              className="p-6"
            >
              {t('dashboard.games.empty')}
            </p>
          )}
          {invalidEdition && (
            <p
              role="alert"
              data-testid="tournament-hall-edition-not-found"
              className="p-6"
            >
              404
            </p>
          )}
          {!editionsQuery.isError
            && editionsQuery.data?.map(item => (
              <article
                key={item.id}
                data-edition-id={item.id}
                className="min-w-0 border-b border-border last:border-b-0"
              >
                <h2>
                  <button
                    type="button"
                    id={`tournament-hall-edition-${item.id}`}
                    data-testid="tournament-hall-edition"
                    data-edition-id={item.id}
                    aria-expanded={selectedEditionId === item.id}
                    aria-controls={`tournament-hall-edition-panel-${item.id}`}
                    aria-current={
                      selectedEditionId === item.id ? 'true' : undefined
                    }
                    onClick={() =>
                      updateState({
                        editionId:
                          selectedEditionId === item.id ? null : item.id,
                      })}
                    className={`grid min-h-24 w-full min-w-0 grid-cols-[minmax(0,1fr)_auto_1rem] items-center gap-3 px-4 py-[18px] text-start transition-colors hover:bg-muted/50 sm:flex sm:flex-nowrap sm:gap-6 sm:px-6 sm:py-[23px] ${focusClassName}`}
                  >
                    <span
                      data-testid={
                        selectedEditionId === item.id
                          ? 'tournament-hall-selected-edition'
                          : undefined
                      }
                      lang={item.contentLanguage}
                      className="col-start-1 row-start-1 min-w-0 break-words sm:flex-1 sm:basis-[300px]"
                    >
                      <span className="block text-[17px] font-semibold leading-normal">
                        {item.content.name}
                      </span>
                      <span className="mt-1 block text-sm font-normal text-muted-foreground">
                        {item.content.title}
                      </span>
                    </span>
                    <span
                      data-testid={
                        selectedEditionId === item.id
                          ? 'tournament-hall-edition-dates'
                          : undefined
                      }
                      data-edition-dates=""
                      className="col-span-3 col-start-1 row-start-2 min-w-0 break-words text-[13px] font-normal tabular-nums text-muted-foreground sm:order-1 sm:flex-none sm:whitespace-nowrap sm:text-sm"
                    >
                      {tournamentDateRange(
                        item.competitionStartsAt,
                        item.competitionEndsAt,
                        item.timezone,
                        language,
                      )}
                      {item.datesPlaceholder
                        && ` · ${t('tournament.placeholder')}`}
                    </span>
                    <span className="col-start-2 row-start-1 shrink-0 justify-self-end whitespace-nowrap rounded-full border border-border px-2.5 py-1 text-xs font-normal sm:order-2">
                      {t(phaseKeys[item.lifecycle.phase])}
                    </span>
                    <ChevronDown
                      className={`col-start-3 row-start-1 size-4 shrink-0 text-muted-foreground transition-transform motion-reduce:transition-none sm:order-3 ${selectedEditionId === item.id ? 'rotate-180' : ''}`}
                      aria-hidden="true"
                    />
                  </button>
                </h2>
                <div
                  id={`tournament-hall-edition-panel-${item.id}`}
                  role="region"
                  aria-labelledby={`tournament-hall-edition-${item.id}`}
                  hidden={selectedEditionId !== item.id}
                  className="min-w-0 border-t border-border px-4 pb-3 sm:px-6 sm:pb-5"
                >
                  {edition?.id === item.id && (
                    <>
                      <div className="flex min-w-0 flex-wrap items-center justify-between gap-x-5 border-b border-border sm:flex-nowrap">
                        {contentAvailable && (
                          <div
                            role="tablist"
                            aria-label={item.content.name}
                            className="flex w-full min-w-0 gap-5 sm:w-auto sm:gap-6"
                          >
                            {contentTabs.map((tab, index) => (
                              <button
                                key={tab}
                                ref={(element) => {
                                  tabRefs.current[index] = element
                                }}
                                type="button"
                                role="tab"
                                id={`tournament-hall-tab-${tab}`}
                                aria-controls={`tournament-hall-panel-${tab}`}
                                aria-selected={selectedTab === tab}
                                tabIndex={
                                  selectedTab === tab
                                  || (selectedTab === 'rules' && index === 0)
                                    ? 0
                                    : -1
                                }
                                data-testid={`tournament-hall-tab-${tab}`}
                                onClick={() => updateState({ tab })}
                                onKeyDown={(event) => {
                                  const direction = language === 'ar' ? -1 : 1
                                  const target
                                    = event.key === 'ArrowRight'
                                      ? (index
                                        + direction
                                        + contentTabs.length)
                                      % contentTabs.length
                                      : event.key === 'ArrowLeft'
                                        ? (index
                                          - direction
                                          + contentTabs.length)
                                        % contentTabs.length
                                        : event.key === 'Home'
                                          ? 0
                                          : event.key === 'End'
                                            ? contentTabs.length - 1
                                            : null
                                  if (target === null)
                                    return
                                  event.preventDefault()
                                  tabRefs.current[target]?.focus()
                                  updateState({ tab: contentTabs[target] })
                                }}
                                className={`min-h-12 min-w-0 border-b-2 text-sm transition-colors ${focusClassName} ${selectedTab === tab ? 'border-foreground font-semibold text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                              >
                                {t(tabKeys[tab])}
                              </button>
                            ))}
                          </div>
                        )}
                        <Link
                          data-testid="tournament-hall-edition-rules"
                          to={`${tournamentUrl(item.id, 'rules')}&${new URLSearchParams({ returnTo: returnTargetFrom(location) })}`}
                          className={`flex min-h-11 shrink-0 items-center gap-1 text-[13px] text-muted-foreground transition-colors hover:text-foreground ${focusClassName}`}
                        >
                          {t('tournamentHall.editionRules')}
                          <ArrowRight
                            className="size-3.5 shrink-0"
                            aria-hidden="true"
                          />
                        </Link>
                      </div>
                      {!contentAvailable
                        && selectedTab !== 'rules'
                        && (edition.lifecycle.publicGamesAvailable
                          && (hallContentQuery.isError || !hallContent) ? (
                              <TournamentHallSupplement
                                edition={edition}
                                tab="results"
                                onRules={() => updateState({ rulesOpen: true })}
                                focusFullRules={false}
                                content={hallContent}
                                loading={hallContentQuery.isPending
                                  || hallContentQuery.isFetching}
                                failed={hallContentQuery.isError}
                                onRetry={() => {
                                  void refetchEditions()
                                  void refetchHallContent()
                                }}
                              />
                            ) : (
                              <p
                                role="status"
                                data-testid="tournament-hall-unpublished"
                                className="flex min-h-20 items-center text-sm text-muted-foreground sm:min-h-[92px]"
                              >
                                {t('tournamentHall.highlightsAfterEnd')}
                              </p>
                            ))}
                      {(contentAvailable || selectedTab === 'rules') && (
                        <>
                          {contentAvailable
                            && contentTabs
                              .filter(tab => tab !== selectedTab)
                              .map(tab => (
                                <div
                                  key={tab}
                                  role="tabpanel"
                                  id={`tournament-hall-panel-${tab}`}
                                  aria-labelledby={`tournament-hall-tab-${tab}`}
                                  hidden
                                />
                              ))}
                          <div
                            role={
                              selectedTab === 'rules' ? 'region' : 'tabpanel'
                            }
                            id={`tournament-hall-panel-${selectedTab}`}
                            aria-labelledby={
                              selectedTab === 'rules'
                                ? `tournament-hall-edition-${item.id}`
                                : `tournament-hall-tab-${selectedTab}`
                            }
                            tabIndex={0}
                            className={`min-w-0 ${selectedTab === 'games' || selectedTab === 'rules' ? 'pt-4' : ''} ${focusClassName}`}
                          >
                            {selectedTab !== 'games'
                              && (rulesOpen && selectedTab === 'rules' ? (
                                <TournamentRulesViewer
                                  key={edition.id}
                                  editionId={edition.id}
                                  onBack={() =>
                                    updateState({ rulesOpen: false })}
                                />
                              ) : (
                                <TournamentHallSupplement
                                  edition={edition}
                                  tab={selectedTab}
                                  onRules={() =>
                                    updateState({ rulesOpen: true })}
                                  focusFullRules={rulesFocusEditionId === edition.id
                                    && !rulesOpen}
                                  content={hallContent}
                                  loading={hallContentQuery.isPending
                                    || hallContentQuery.isFetching
                                    || editionsQuery.isFetching}
                                  failed={hallContentQuery.isError}
                                  onRetry={() => {
                                    void refetchEditions()
                                    void refetchHallContent()
                                  }}
                                />
                              ))}
                            {selectedTab === 'games' && (
                              <>
                                <div className="mb-4 flex min-w-0 flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                                  <div>
                                    <h3 className="text-lg font-semibold">
                                      {t('tournamentHall.allGamesTab')}
                                    </h3>
                                  </div>
                                  <form
                                    role="search"
                                    data-testid="tournament-hall-search-form"
                                    className="flex w-full min-w-0 gap-2 xl:max-w-sm"
                                    onSubmit={(event) => {
                                      event.preventDefault()
                                      updateState({
                                        search: draftSearch.trim(),
                                      })
                                    }}
                                  >
                                    <label
                                      className="sr-only"
                                      htmlFor="tournament-hall-search"
                                    >
                                      {t('tournamentHall.search')}
                                    </label>
                                    <input
                                      ref={searchRef}
                                      id="tournament-hall-search"
                                      type="search"
                                      maxLength={200}
                                      value={draftSearch}
                                      onChange={event =>
                                        setDraftSearch(event.target.value)}
                                      placeholder={t('tournamentHall.search')}
                                      data-testid="tournament-hall-search"
                                      className={`min-h-11 w-full min-w-0 rounded-lg border border-border bg-muted/50 px-3 text-base text-foreground placeholder:text-muted-foreground ${focusClassName}`}
                                    />
                                    <button
                                      type="submit"
                                      aria-label={t('tournamentHall.search')}
                                      className={`${controlClassName} flex min-w-11 items-center justify-center px-2`}
                                      data-testid="tournament-hall-search-submit"
                                    >
                                      <Search
                                        className="size-5"
                                        aria-hidden="true"
                                      />
                                    </button>
                                    {(draftSearch || state.search) && (
                                      <button
                                        type="button"
                                        aria-label={t(
                                          'play.roleHypotheses.clear',
                                        )}
                                        data-testid="tournament-hall-search-reset"
                                        className={`${controlClassName} flex min-w-11 items-center justify-center px-2`}
                                        onClick={() => {
                                          setDraftSearch('')
                                          updateState({ search: '' })
                                          searchRef.current?.focus()
                                        }}
                                      >
                                        <X
                                          className="size-5"
                                          aria-hidden="true"
                                        />
                                      </button>
                                    )}
                                  </form>
                                </div>
                                {archiveQuery.isError && (
                                  <div
                                    role="alert"
                                    data-testid="tournament-hall-error"
                                    className="py-8 text-center"
                                  >
                                    <p>{t('forum.errorGeneric')}</p>
                                    <button
                                      type="button"
                                      data-testid="tournament-hall-retry"
                                      onClick={() => {
                                        void refetchEditions()
                                        void refetchArchive()
                                      }}
                                      className={`${controlClassName} mt-3`}
                                    >
                                      {t('play.retry.button')}
                                    </button>
                                  </div>
                                )}
                                {!archiveQuery.isError && loadingArchive && (
                                  <p
                                    role="status"
                                    data-testid="tournament-hall-loading"
                                    className="flex items-center justify-center gap-2 py-8"
                                  >
                                    <Loader2
                                      className="size-5 animate-spin"
                                      aria-hidden="true"
                                    />
                                    {t('forum.loading')}
                                  </p>
                                )}
                                {!archiveQuery.isError
                                  && !loadingArchive
                                  && !publicGamesAvailable && (
                                  <p
                                    role="status"
                                    data-testid="tournament-hall-unpublished"
                                    className="py-8 text-center text-primary"
                                  >
                                    {t('tournamentHall.publication')}
                                  </p>
                                )}
                                {!archiveQuery.isError
                                  && !loadingArchive
                                  && publicGamesAvailable
                                  && archive && (
                                  <>
                                    {archive.records.length === 0 ? (
                                      <p
                                        role="status"
                                        data-testid="tournament-hall-empty"
                                        className="py-8 text-center text-muted-foreground"
                                      >
                                        {t(
                                          state.search
                                            ? 'dashboard.games.emptyFilter'
                                            : 'dashboard.games.empty',
                                        )}
                                      </p>
                                    ) : (
                                      <div
                                        role="table"
                                        aria-label={t(
                                          'tournamentHall.allGamesTab',
                                        )}
                                        className="overflow-hidden rounded-lg border border-border"
                                      >
                                        <div
                                          role="row"
                                          className="hidden grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1fr)_8rem] gap-3 border-b border-border bg-muted/50 px-4 py-3 text-sm font-semibold text-muted-foreground md:grid"
                                        >
                                          <span role="columnheader">
                                            {t('tournamentHall.player')}
                                          </span>
                                          <span role="columnheader">
                                            {t('tournamentHall.role')}
                                          </span>
                                          <span role="columnheader">
                                            {t('tournamentHall.result')}
                                          </span>
                                          <span role="columnheader">
                                            {t('tournamentHall.replay')}
                                          </span>
                                        </div>
                                        {archive.records.map(record => (
                                          <div
                                            key={record.id}
                                            role="row"
                                            data-testid="tournament-hall-record"
                                            data-edition-id={edition.id}
                                            data-participation-id={record.id}
                                            data-game-id={record.gameId}
                                            data-category-id={record.categoryId}
                                            data-state={record.state}
                                            className="grid min-w-0 gap-3 border-b border-border bg-muted/50 p-4 last:border-b-0 sm:grid-cols-2 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1fr)_8rem] md:items-center"
                                          >
                                            <div
                                              role="cell"
                                              className="min-w-0 break-words"
                                            >
                                              <span className="mb-1 block text-sm text-muted-foreground md:hidden">
                                                {t('tournamentHall.player')}
                                              </span>
                                              <span className="font-semibold">
                                                {record.participant.name}
                                              </span>
                                              <span className="mt-2 block text-sm text-muted-foreground">
                                                {new Intl.DateTimeFormat(
                                                  language,
                                                  {
                                                    timeZone:
                                                        edition.timezone,
                                                    dateStyle: 'medium',
                                                    timeStyle: 'short',
                                                  },
                                                ).format(
                                                  new Date(record.createdAt),
                                                )}
                                              </span>
                                            </div>
                                            <div
                                              role="cell"
                                              className="min-w-0 break-words"
                                            >
                                              <span className="mb-1 block text-sm text-muted-foreground md:hidden">
                                                {t('tournamentHall.role')}
                                              </span>
                                              {t(`game.${record.role}`)}
                                              <span className="mt-2 block text-sm text-muted-foreground">
                                                {t(
                                                  record.role
                                                  === 'mechanical_wolf'
                                                    ? 'game.mechanical_wolf'
                                                    : 'tournamentParticipation.godRoleCategory',
                                                )}
                                              </span>
                                            </div>
                                            <div
                                              role="cell"
                                              className="min-w-0 break-words"
                                            >
                                              <span className="mb-1 block text-sm text-muted-foreground md:hidden">
                                                {t('tournamentHall.result')}
                                              </span>
                                              <span
                                                className={
                                                  record.state
                                                  === 'COMPLETED_WIN'
                                                    ? 'font-semibold text-primary'
                                                    : 'text-foreground'
                                                }
                                              >
                                                {recordState(record)}
                                              </span>
                                              {record.state === 'VOID' && (
                                                <span className="mt-2 block text-sm text-muted-foreground">
                                                  {t(
                                                    'tournamentVoid.noResult',
                                                  )}
                                                </span>
                                              )}
                                            </div>
                                            <div role="cell">
                                              <Link
                                                data-testid="tournament-hall-replay"
                                                to={`/play/${record.gameId}?${new URLSearchParams({ tournament: edition.id, view: 'god', returnTo: returnTargetFrom(location) })}`}
                                                className={`${controlClassName} flex items-center justify-center gap-1 text-center text-sm text-primary`}
                                              >
                                                {t(
                                                  'publicPlayerProfile.viewGame',
                                                )}
                                                <ChevronRight
                                                  className="size-4 shrink-0"
                                                  aria-hidden="true"
                                                />
                                              </Link>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                    <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
                                      <p
                                        role="status"
                                        data-testid="tournament-hall-record-count"
                                        className="text-sm text-muted-foreground"
                                      >
                                        {t('tournamentHall.recordCount', {
                                          count: archive.total,
                                        })}
                                      </p>
                                      {archive.pageCount > 0 && (
                                        <nav
                                          aria-label={t('forum.page', {
                                            page: archive.page,
                                            total: archive.pageCount,
                                          })}
                                          className="flex flex-wrap items-center gap-2"
                                        >
                                          <button
                                            type="button"
                                            data-testid="tournament-hall-previous"
                                            disabled={archive.page <= 1}
                                            onClick={() =>
                                              updateState({
                                                page: archive.page - 1,
                                              })}
                                            className={`${controlClassName} flex items-center gap-1 text-sm`}
                                          >
                                            <ChevronLeft
                                              className="size-4 shrink-0"
                                              aria-hidden="true"
                                            />
                                            {t('forum.prev')}
                                          </button>
                                          <span
                                            data-testid="tournament-hall-page"
                                            className="px-1 text-sm text-muted-foreground"
                                          >
                                            {t('forum.page', {
                                              page: archive.page,
                                              total: archive.pageCount,
                                            })}
                                          </span>
                                          <button
                                            type="button"
                                            data-testid="tournament-hall-next"
                                            disabled={archive.page
                                              >= archive.pageCount}
                                            onClick={() =>
                                              updateState({
                                                page: archive.page + 1,
                                              })}
                                            className={`${controlClassName} flex items-center gap-1 text-sm`}
                                          >
                                            {t('forum.next')}
                                            <ChevronRight
                                              className="size-4 shrink-0"
                                              aria-hidden="true"
                                            />
                                          </button>
                                        </nav>
                                      )}
                                    </div>
                                  </>
                                )}
                              </>
                            )}
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              </article>
            ))}
        </section>
      </div>
    </main>
  )
}
